import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { SUBJECT_META } from "@/lib/scale";
import { Plus, Loader2, Trash2, Sparkles, NotebookPen, Wand2 } from "lucide-react";

const SUBJECT_OPTS = [
  { key: "general", name: "General" },
  { key: "literacy", name: "Literacy" },
  { key: "numeracy", name: "Numeracy" },
  { key: "social_emotional", name: "Social & Emotional" },
];

export default function ObservationsSection({ studentId }) {
  const [obs, setObs] = useState(null);
  const [subject, setSubject] = useState("general");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [expanding, setExpanding] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const load = () => api.get(`/observations?student_id=${studentId}`).then((r) => setObs(r.data)).catch(() => setObs([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [studentId]);

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post("/observations", { student_id: studentId, subject, text });
      setText("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const expand = async () => {
    if (!text.trim()) { toast.error("Write a few quick notes first"); return; }
    setExpanding(true);
    try {
      const { data } = await api.post("/observations/expand", { notes: text, subject });
      setText(data.text);
      toast.success("Note expanded — edit if needed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setExpanding(false); }
  };

  const remove = async (id) => { await api.delete(`/observations/${id}`); load(); };

  const getSuggestions = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(`/students/${studentId}/observation-suggestions`);
      setSuggestions(data.suggestions);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSuggesting(false); }
  };

  return (
    <div className="space-y-6">
      {/* Add */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6">
        <h3 className="font-['Outfit'] text-lg font-bold text-slate-800 mb-4 flex items-center gap-2"><NotebookPen size={20} className="text-[#9A7AA0]" /> Add an observation</h3>
        <div className="flex gap-1.5 mb-3 flex-wrap">
          {SUBJECT_OPTS.map((o) => (
            <button key={o.key} data-testid={`obs-subject-${o.key}`} onClick={() => setSubject(o.key)}
              className={`text-sm rounded-full px-3 py-1.5 font-medium transition border ${subject === o.key ? "bg-[#4A5D6B] border-[#4A5D6B] text-white" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
              {o.name}
            </button>
          ))}
        </div>
        <textarea data-testid="obs-text-input" rows={3} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Jot a quick note — e.g. 'kept losing place when reading, used finger to track'"
          className="w-full bg-[#F9F9F7] border border-slate-200 rounded-xl px-3 py-2.5 outline-none focus:border-[#81B29A] focus:bg-white resize-none" />
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button data-testid="obs-expand-btn" onClick={expand} disabled={expanding}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] text-white transition disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#81B29A,#5d8a73)" }}>
            {expanding ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Expand with AI
          </button>
          <button data-testid="obs-save-btn" onClick={add} disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] bg-[#4A5D6B] text-white hover:bg-[#3A4A56] transition disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Save observation
          </button>
        </div>
      </div>

      {/* AI suggestions */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-['Outfit'] text-lg font-bold text-slate-800 flex items-center gap-2"><Sparkles size={18} className="text-[#5d8a73]" /> AI next-step suggestions</h3>
          <button data-testid="obs-suggest-btn" onClick={getSuggestions} disabled={suggesting}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] text-white transition disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#81B29A,#5d8a73)" }}>
            {suggesting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Suggest from observations
          </button>
        </div>
        {suggestions.length > 0 ? (
          <ul className="mt-4 space-y-2" data-testid="obs-suggestions">
            {suggestions.map((s, i) => (
              <li key={`${i}-${s.slice(0, 24)}`} className="flex items-start gap-2 text-sm text-slate-700 bg-[#81B29A]/5 border border-[#81B29A]/20 rounded-lg p-3">
                <Sparkles size={14} className="mt-0.5 shrink-0 text-[#5d8a73]" />{s}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400 mt-3">Generate practical next steps based on this student's observation log.</p>
        )}
      </div>

      {/* Log */}
      {obs === null ? (
        <div className="flex justify-center py-10 text-slate-300"><Loader2 className="animate-spin" /></div>
      ) : obs.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6">No observations yet.</p>
      ) : (
        <div className="space-y-2.5">
          {obs.map((o) => {
            const meta = SUBJECT_META[o.subject];
            return (
              <div key={o.id} data-testid={`obs-${o.id}`} className="group bg-white rounded-xl border border-slate-100 p-4 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: meta?.tint || "#94a3b8" }}>{meta?.name || "General"}</span>
                    <span className="text-xs text-slate-400">{new Date(o.date).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" })}</span>
                  </div>
                  <p className="text-sm text-slate-700">{o.text}</p>
                </div>
                <button data-testid={`delete-obs-${o.id}`} onClick={() => remove(o.id)} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition"><Trash2 size={15} /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
